import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  Sparkles,
  Zap,
  Users,
  Target,
  Flame,
  CalendarClock,
  Percent,
  AlertTriangle,
  MessageCircle,
  Mail,
  MonitorSmartphone,
  Phone,
  Filter,
  ShieldAlert,
  Bot,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Save,
  Lock,
  Loader2,
  CheckCircle2,
  Search,
  X,
  FileText,
  BrainCircuit,
} from 'lucide-react';
import type { User, Vacancy } from '../types';
import { UserRole } from '../types';
import { parsePortalClientProposal } from '../utils/portalProposalThread';
import { BulkWhatsAppComposerModal } from './BulkWhatsAppComposerModal';
import BulkEmailComposerModal from './BulkEmailComposerModal';
import CandidateDetailModal from './CandidateDetailModal';
import {
  supabase,
  supabase_get_paginated_talent_pool,
  supabase_get_candidate_communications,
} from '../services/supabaseService';
import type { EngagementTask } from './candidateEngagement/types';
import type { UserQuickEngagementButton } from './candidateEngagement/quickEngagementButtons';
import {
  buildTasksFromQuickButton,
  hasCandidateEngagementConsent,
  isCandidateMissingAvailability,
  isAvailabilityInExpiringWindow,
  isAvailabilityDateWithinNextDays,
  parseCandidateAvailabilityDate,
} from './candidateEngagement/quickEngagementButtons';
import {
  isWebsiteWebhookInboundApplication,
  websiteWebhookSourceDisplayLabel,
} from '../utils/candidateJourneyBuckets';
import {
  fetchEngagementClaim,
  releaseEngagementClaim,
  tryAcquireEngagementClaim,
  type EngagementClaim,
} from './candidateEngagement/engagementClaims';
import {
  buildAssignmentEndings,
  buildDataConsentRows,
  buildLiveTodayTasks,
  summarizeTasksForKpis,
  taskToBulkCandidate,
  minimalTaskForContact,
} from './candidateEngagement/liveEngagement';
type MainTab =
  | 'today'
  | 'open_application'
  | 'proposed'
  | 'availability'
  | 'ending'
  | 'field'
  | 'data'
  | 'birthday';

const DEFAULT_FIELD_MILESTONE_MONTHS = [1, 3, 6, 9, 12, 15, 18, 21, 24];

function loadDismissedTaskIds(userId: string): Set<string> {
  try {
    const raw = localStorage.getItem(`wt_engagement_done_${userId}`);
    const a = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(a) ? a : []);
  } catch {
    return new Set();
  }
}

function pickVacancyRecruiterId(v: any): string | null {
  if (!v) return null;
  const raw =
    v.data?.contactPersonId ||
    v.contactPersonId ||
    v.data?.recruiter_id ||
    v.data?.assignedRecruiterId ||
    v.assignedRecruiterId ||
    v.assigned_recruiter_id ||
    null;
  const s = String(raw ?? '').trim();
  return s || null;
}

function isOpenSollicitatieStatus(status: string | undefined | null): boolean {
  const s = (status || '').toLowerCase();
  if (!s || s === 'rejected' || s.includes('afgewezen') || s.includes('ingetrokken')) return false;
  if (s === 'draft_cv') return false;
  if (s.includes('voorgesteld') || s.includes('proposed') || s.includes('submit')) return false;
  if (s.includes('gesprek') || s.includes('interview') || s.includes('i-view')) return false;
  if (s.includes('aanbod') || s.includes('offer')) return false;
  if (s.includes('plaatsing') || s.includes('placed') || s.includes('hired') || s.includes('aangenomen')) return false;
  return true;
}

function isVoorgesteldStatus(status: string | undefined | null): boolean {
  const s = (status || '').toLowerCase();
  return s.includes('voorgesteld') || s.includes('proposed') || s.includes('submit');
}

function parseVoorgesteldDateFromApp(app: any): Date | null {
  const raw =
    app?.pipeline_meta?.voorgesteld_date ||
    app?.cv_snapshot?.pipeline_meta?.voorgesteld_date ||
    app?.cv_snapshot?.pipelineMeta?.voorgesteld_date ||
    app?.data?.voorgesteld_date;
  if (raw == null) return null;
  const d = new Date(String(raw).slice(0, 10) + 'T12:00:00');
  return Number.isNaN(d.getTime()) ? null : d;
}

const APPLICATIONS_SELECT_LITE =
  'id,status,vacancy_id,candidate_id,cv_summary,cv_snapshot,source_client_id,created_at,updated_at,candidates(id,name,email,phone,tags,ai_parsed_data),vacancies(id,title,client_id,data,assigned_recruiter_id,eindklant_id),source_client:clients!source_client_id(name)';

const PLACEMENTS_SELECT_LITE =
  'id,status,start_date,end_date,vacancy_id,candidate_id,client_id,eindklant_id,opdrachtgever_id,created_at,candidate:candidates(id,name,email,phone,availability,tags,ai_parsed_data,updated_at),vacancy:vacancies(id,title,client_id,data,assigned_recruiter_id,eindklant_id,client:clients!vacancies_client_id_fkey(id,name),eindklant:clients!vacancies_eindklant_id_fkey(id,name)),eindklant:clients!placements_eindklant_id_fkey(id,name),opdrachtgever:clients!placements_opdrachtgever_id_fkey(id,name)';

async function fetchLeanPlacementsForEngagement(user: { role: UserRole; clientId?: string | null }): Promise<any[]> {
  const portalFilter =
    user.role === UserRole.CLIENT || user.role === UserRole.CLIENT_RECRUITER ? user.clientId : null;
  try {
    let q = supabase
      .from('placements')
      .select(PLACEMENTS_SELECT_LITE)
      .order('end_date', { ascending: true, nullsFirst: false })
      .limit(800);
    if (portalFilter) {
      q = q.or(
        `client_id.eq.${portalFilter},eindklant_id.eq.${portalFilter},opdrachtgever_id.eq.${portalFilter}`
      );
    }
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  } catch (e) {
    console.warn('[CandidateEngagementCenter] lean placements query failed', e);
    return [];
  }
}

const APPS_DB_EXCLUDE_STATUSES = '("Rejected","Afgewezen","Ingetrokken","Niet voorgesteld","Draft_CV","Niet gehired")';

const ENGAGEMENT_CACHE_TTL_MS = 5 * 60 * 1000;

function engagementCacheKey(kind: 'pool' | 'placements' | 'apps', user: { id: string; clientId?: string | null }) {
  return `wt_engagement_cache_v1_${kind}_${user.id || 'anon'}_${user.clientId || 'na'}`;
}

function loadEngagementCache<T>(kind: 'pool' | 'placements' | 'apps', user: { id: string; clientId?: string | null }): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(engagementCacheKey(kind, user));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts?: number; data?: T };
    if (!parsed?.ts || Date.now() - parsed.ts > ENGAGEMENT_CACHE_TTL_MS) return null;
    return (parsed.data ?? null) as T | null;
  } catch {
    return null;
  }
}

function saveEngagementCache<T>(kind: 'pool' | 'placements' | 'apps', user: { id: string; clientId?: string | null }, data: T) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      engagementCacheKey(kind, user),
      JSON.stringify({ ts: Date.now(), data })
    );
  } catch {
    /* sessionStorage quota or serialization error — ignore */
  }
}

function formatDurationSinceNl(iso?: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days < 0) return 'toekomstig';
  if (days === 0) return 'vandaag';
  if (days === 1) return '1 dag';
  if (days < 14) return `${days} dagen`;
  const weeks = Math.floor(days / 7);
  if (weeks < 9) return `${weeks} wkn`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} mnd`;
  const years = (days / 365).toFixed(1).replace('.', ',');
  return `${years} jr`;
}

/** Relatieve tijd sinds sollicitatie (minuten / uren / dagen). */
function formatRelativeTimeNl(iso?: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const sec = Math.floor((Date.now() - t) / 1000);
  if (sec < 0) return 'toekomstig';
  if (sec < 60) return 'zojuist';
  if (sec < 3600) return `${Math.floor(sec / 60)} min geleden`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} uur geleden`;
  return formatDurationSinceNl(iso);
}

function fullCalendarMonthsSince(startIso: string | null | undefined): number {
  if (!startIso) return 0;
  const s = new Date(startIso);
  if (Number.isNaN(s.getTime())) return 0;
  const e = new Date();
  let months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  if (e.getDate() < s.getDate()) months -= 1;
  return Math.max(0, months);
}

interface CandidateEngagementCenterProps {
  user: User;
  vacancies: Vacancy[];
  candidates: any[];
  onStartCall?: (profile: { id: string; candidate: any }) => void;
  onRefreshPool?: () => void;
  /** Compacte layout (sidebar verborgen) wanneer `?cockpit=1` op Engagement. */
  onCockpitLayoutMode?: (compact: boolean) => void;
}

const CandidateEngagementCenter: React.FC<CandidateEngagementCenterProps> = ({
  user,
  vacancies,
  candidates: candidatesProp,
  onStartCall,
  onRefreshPool,
  onCockpitLayoutMode,
}) => {
  const [isCockpitWindow] = useState(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('cockpit') === '1'
  );
  const [mainTab, setMainTab] = useState<MainTab>('today');
  const cachedPoolInit = useMemo(
    () => (user?.id ? loadEngagementCache<any[]>('pool', user) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id, user?.clientId]
  );
  const cachedPlacementsInit = useMemo(
    () => (user?.id ? loadEngagementCache<any[]>('placements', user) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id, user?.clientId]
  );
  const cachedAppsInit = useMemo(
    () => (user?.id ? loadEngagementCache<any[]>('apps', user) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id, user?.clientId]
  );
  const [poolRows, setPoolRows] = useState<any[]>(() => cachedPoolInit || []);
  const [placements, setPlacements] = useState<any[]>(() => cachedPlacementsInit || []);
  const [dataLoading, setDataLoading] = useState(() => !(cachedPoolInit?.length && cachedPlacementsInit !== null));
  const [detailModal, setDetailModal] = useState<{
    candidateId: string;
    vacancyId?: string;
    initialTab?: 'overview' | 'comm' | 'vacatureflow' | 'documents' | 'ai_analysis';
  } | null>(null);
  const [actionTask, setActionTask] = useState<EngagementTask | null>(null);
  const [webhookInboxExpandedAppId, setWebhookInboxExpandedAppId] = useState<string | null>(null);
  const [engagementApplications, setEngagementApplications] = useState<any[]>(() => cachedAppsInit || []);
  const [engagementAppsLoading, setEngagementAppsLoading] = useState(() => !cachedAppsInit);
  const [loadProgress, setLoadProgress] = useState<{
    active: boolean;
    label: string;
    /** null = indeterminate animation; number 0-100 = determinate */
    percent: number | null;
    detail?: string;
  }>({ active: false, label: '', percent: null });
  const [listSearch, setListSearch] = useState<string>('');
  const [recruiters, setRecruiters] = useState<{ id: string; name: string | null; email: string | null; role: string }[]>([]);
  const [filterRecruiterId, setFilterRecruiterId] = useState<string>('');
  const [dismissedTaskIds, setDismissedTaskIds] = useState<Set<string>>(() =>
    user?.id ? loadDismissedTaskIds(user.id) : new Set()
  );
  const [fieldMilestoneMonths, setFieldMilestoneMonths] = useState<number[]>(DEFAULT_FIELD_MILESTONE_MONTHS);
  const [fieldMilestoneFilter, setFieldMilestoneFilter] = useState<number | 'all'>('all');
  const [availabilitySubTab, setAvailabilitySubTab] = useState<'upcoming_month' | 'unknown' | 'expiring_availability'>('upcoming_month');
  const [birthdaySubTab, setBirthdaySubTab] = useState<'today' | 'week'>('week');
  const [toast, setToast] = useState<string | null>(null);
  const [waModalOpen, setWaModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [expandedCandidateId, setExpandedCandidateId] = useState<string | null>(null);
  const [commsCache, setCommsCache] = useState<Record<string, any[]>>({});
  const [quickLogDraft, setQuickLogDraft] = useState<Record<string, { type: 'note' | 'whatsapp' | 'email' | 'phone'; text: string }>>({});
  const [inlineNotesDraft, setInlineNotesDraft] = useState<Record<string, string>>({});
  const [inlineNotesSaving, setInlineNotesSaving] = useState<Record<string, boolean>>({});
  const [commFocusIndex, setCommFocusIndex] = useState<Record<string, number>>({});
  const [contactClaims, setContactClaims] = useState<Record<string, EngagementClaim | null>>({});
  const [claimBusyId, setClaimBusyId] = useState<string | null>(null);
  const [availabilityDateDraft, setAvailabilityDateDraft] = useState<Record<string, string>>({});
  const [availabilityDateSaving, setAvailabilityDateSaving] = useState<Record<string, boolean>>({});
  const prevExpandedRef = React.useRef<string | null>(null);
  /** Voorkomt volledige pool-refresh bij elke parent re-render (nieuwe array-ref voor `candidates`). */
  const candidatesPropRef = useRef(candidatesProp);
  candidatesPropRef.current = candidatesProp;
  const commsCacheRef = useRef(commsCache);
  commsCacheRef.current = commsCache;

  const todayTasks = useMemo(
    () => buildLiveTodayTasks(poolRows, placements, vacancies || [], user),
    [poolRows, placements, vacancies, user]
  );

  const assignmentEndings = useMemo(() => buildAssignmentEndings(placements), [placements]);
  const dataConsentRows = useMemo(() => buildDataConsentRows(poolRows), [poolRows]);

  const kpiStats = useMemo(
    () => summarizeTasksForKpis(todayTasks, poolRows, placements, assignmentEndings),
    [todayTasks, poolRows, placements, assignmentEndings]
  );

  const selectedVacancy = useMemo(() => {
    const vid = actionTask?.vacancy_id;
    return (vacancies || []).find((v) => v.id === vid) || null;
  }, [vacancies, actionTask?.vacancy_id]);

  const bulkSelection = useMemo(() => {
    if (!actionTask) return [];
    return [taskToBulkCandidate(actionTask, selectedVacancy)];
  }, [actionTask, selectedVacancy]);

  const getPoolCandidate = useCallback(
    (candidateId: string) => {
      const row = poolRows.find((r) => (r.candidate?.id || r.id) === candidateId);
      return row?.candidate || null;
    },
    [poolRows]
  );

  const endingEngagementTasks = useMemo((): EngagementTask[] => {
    return assignmentEndings.map((a) => {
      const placementId = String(a.id || '').startsWith('end-') ? String(a.id).slice(4) : String(a.id);
      const relatedPl = placements.find((p) => String(p.id) === placementId);
      const cand = getPoolCandidate(a.candidate_id);
      // Fallback: assignment endings can include candidates that are not (yet) in the loaded poolRows slice.
      // Placements often has richer candidate contact data, so use that to keep contact buttons enabled.
      const placementCand =
        cand ||
        placements.find((p) => p?.candidate?.id === a.candidate_id)?.candidate ||
        relatedPl?.candidate ||
        null;
      const t = minimalTaskForContact(
        {
          id: a.candidate_id,
          name: a.candidate_name,
          email: placementCand?.email,
          phone: placementCand?.phone,
          opted_out: false,
        },
        null,
        user,
        {
          reason: `Einde opdracht · ${a.assignment}`,
          message: `Hoi ${(a.candidate_name || '').split(/\s+/)[0] || ''},\n\nJe opdracht bij ${a.client} (${a.assignment}) eindigt rond ${a.end_date}. Laten we je beschikbaarheid en vervolg afstemmen.\n\nGroet,\n${user.name || ''}`,
        }
      );
      return {
        ...t,
        id: `assignment-ending-${a.id}`,
        task_type: 'assignment_ending',
        engagement_score: Math.max(15, 100 - Math.min(95, a.days_left)),
        current_status: `Nog ${a.days_left} dgn`,
        reason: `${a.client} · ${a.assignment} · eind ${a.end_date} (${a.days_left} dgn)`,
        vacancy_id: relatedPl?.vacancy_id || t.vacancy_id,
        statusSinceIso: relatedPl?.start_date || relatedPl?.created_at || null,
        ownerRecruiterId: pickVacancyRecruiterId(relatedPl?.vacancy) ?? null,
      };
    });
  }, [assignmentEndings, getPoolCandidate, placements, user]);

  const dataConsentEngagementTasks = useMemo((): EngagementTask[] => {
    return dataConsentRows.map((d) => {
      const dc = getPoolCandidate(d.candidate_id);
      const t = minimalTaskForContact(
        {
          id: d.candidate_id,
          name: d.name,
          email: dc?.email,
          phone: dc?.phone,
          opted_out: d.opted_out,
        },
        null,
        user,
        {
          reason: 'Data & consent',
          message: `Hoi ${(d.name || '').split(/\s+/)[0] || ''},\n\nWe willen je gegevens graag up-to-date houden. Kun je even reageren op beschikbaarheid of consent?\n\nGroet,\n${user.name || ''}`,
        }
      );
      return {
        ...t,
        id: `data-consent-${d.candidate_id}`,
        task_type: 'consent_renewal',
        engagement_score: d.gdpr_ok ? 72 : 28,
        current_status: d.gdpr_ok ? `Consent ${d.consent_expiry ? `tot ${d.consent_expiry}` : 'OK'}` : 'Consent actie',
        statusSinceIso: dc?.updated_at || null,
        reason: d.gdpr_ok
          ? `CV ${d.cv_updated || '—'} · besch. ${d.availability_updated || '—'} · salaris ${d.salary_updated || '—'}`
          : `Actie nodig · CV ${d.cv_updated || '—'} · besch. ${d.availability_updated || '—'}`,
      };
    });
  }, [dataConsentRows, getPoolCandidate, user]);

  const synthQuickButton = useCallback(
    (partial: Omit<UserQuickEngagementButton, 'id' | 'createdAt'>): UserQuickEngagementButton => ({
      ...partial,
      id: `_tab_${partial.filterType}_${partial.title}`,
      createdAt: new Date().toISOString(),
    }),
    []
  );

  const birthdayTasks = useMemo(
    () =>
      buildTasksFromQuickButton(
        poolRows,
        synthQuickButton({
          title: birthdaySubTab === 'today' ? 'Verjaardag vandaag' : 'Verjaardag komende week',
          filterType: birthdaySubTab === 'today' ? 'birthday_today' : 'birthday_week',
        }),
        user
      ),
    [poolRows, user, birthdaySubTab, synthQuickButton]
  );

  const availabilityUnknownTasks = useMemo(() => {
    const rows: EngagementTask[] = [];
    for (const pr of poolRows) {
      const c = pr?.candidate || pr;
      if (!c?.id || !hasCandidateEngagementConsent(c)) continue;
      if (!isCandidateMissingAvailability(c)) continue;
      const t = minimalTaskForContact(
        {
          id: c.id,
          name: c.name || 'Kandidaat',
          email: c.email,
          phone: c.phone,
          opted_out: false,
        },
        null,
        user,
        {
          reason: 'Beschikbaarheid onbekend',
          message: `Hoi ${(c.name || '').split(/\s+/)[0] || ''},\n\nKun je je beschikbaarheid even laten weten? Dan kan ik je beter matchen.\n\nGroet,\n${user.name || ''}`,
        }
      );
      t.id = `avail-unknown-${c.id}`;
      t.task_type = 'availability_check';
      t.statusSinceIso = c.updated_at || null;
      rows.push(t);
    }
    rows.sort((a, b) => (a.candidate_name || '').localeCompare(b.candidate_name || '', 'nl'));
    return rows;
  }, [poolRows, user]);

  const availabilityExpiringTasks = useMemo(() => {
    const rows: EngagementTask[] = [];
    for (const pr of poolRows) {
      const c = pr?.candidate || pr;
      if (!c?.id || !hasCandidateEngagementConsent(c)) continue;
      if (!isAvailabilityInExpiringWindow(c)) continue;
      const first = (c.name || '').split(/\s+/)[0] || '';
      const t = minimalTaskForContact(
        {
          id: c.id,
          name: c.name || 'Kandidaat',
          email: c.email,
          phone: c.phone,
          opted_out: false,
        },
        null,
        user,
        {
          reason: 'Beschikbaarheid (datum) vernieuwen',
          message: `Hoi ${first},\n\nJe beschikbaarheid in ons systeem is of wordt binnenkort actueel. Kun je bevestigen vanaf wanneer je inzetbaar bent?\n\nGroet,\n${user.name || ''}`,
        }
      );
      t.id = `avail-expiring-${c.id}`;
      t.task_type = 'availability_check';
      t.statusSinceIso = c.updated_at || null;
      rows.push(t);
    }
    rows.sort((a, b) => (a.candidate_name || '').localeCompare(b.candidate_name || '', 'nl'));
    return rows;
  }, [poolRows, user]);

  const availabilityUpcomingMonthTasks = useMemo(() => {
    const rows: EngagementTask[] = [];
    for (const pr of poolRows) {
      const c = pr?.candidate || pr;
      if (!c?.id || !hasCandidateEngagementConsent(c)) continue;
      if (!isAvailabilityDateWithinNextDays(c, 31)) continue;
      const first = (c.name || '').split(/\s+/)[0] || '';
      const d = parseCandidateAvailabilityDate(c);
      const dLabel = d ? d.toLocaleDateString('nl-NL') : '—';
      const t = minimalTaskForContact(
        {
          id: c.id,
          name: c.name || 'Kandidaat',
          email: c.email,
          phone: c.phone,
          opted_out: false,
        },
        null,
        user,
        {
          reason: `Beschikbaar per ${dLabel} (binnen ca. 1 maand)`,
          message: `Hoi ${first},\n\nJe staat bij ons ingeroosterd als beschikbaar rond ${dLabel}. Klopt dat nog, of wil je iets afstemmen?\n\nGroet,\n${user.name || ''}`,
        }
      );
      t.id = `avail-upcoming-${c.id}`;
      t.task_type = 'availability_check';
      t.current_status = `Per ${dLabel}`;
      t.statusSinceIso = c.updated_at || null;
      rows.push(t);
    }
    rows.sort((a, b) => (a.candidate_name || '').localeCompare(b.candidate_name || '', 'nl'));
    return rows;
  }, [poolRows, user]);

  const proposedApplicationTasks = useMemo((): EngagementTask[] => {
    const out: EngagementTask[] = [];
    for (const app of engagementApplications) {
      if (!isVoorgesteldStatus(app.status)) continue;
      const cand = app.candidates ?? app.candidate;
      const c = Array.isArray(cand) ? cand[0] : cand;
      if (!c?.id) continue;
      if (!hasCandidateEngagementConsent(c)) continue;
      const vacRaw = app.vacancies ?? app.vacancy;
      const v = Array.isArray(vacRaw) ? vacRaw[0] : vacRaw;
      const vacObj = v
        ? ({
            id: v.id,
            clientId: v.client_id,
            title: v.title,
            status: v.status,
            data: v.data || {},
            createdAt: v.created_at,
            updatedAt: v.updated_at,
            description: '',
            responsibilities: {},
          } as Vacancy)
        : null;
      const voorgesteld = parseVoorgesteldDateFromApp(app);
      const daysStuck =
        voorgesteld != null ? Math.floor((Date.now() - voorgesteld.getTime()) / 86400000) : null;
      const stale = daysStuck !== null && daysStuck > 4;
      const statusLine =
        voorgesteld != null
          ? `Voorgesteld ${voorgesteld.toLocaleDateString('nl-NL')} · ${daysStuck ?? '—'} dgn op voorstel`
          : 'Voorgesteld (datum onbekend — zet voorgesteld_date)';
      const t = minimalTaskForContact(
        { id: c.id, name: c.name, email: c.email, phone: c.phone, opted_out: false },
        vacObj,
        user,
        {
          reason: stale
            ? `⚠️ >4 dagen op voorstel · ${v?.title || 'Vacature'}`
            : `Voorstel · ${v?.title || 'Vacature'}`,
          message: `Hoi ${(c.name || '').split(/\s+/)[0] || ''},\n\nIk wil even afstemmen hoe het voorstel bij de klant ligt en wat je volgende stap is.\n\nGroet,\n${user.name || ''}`,
        }
      );
      out.push({
        ...t,
        id: `proposed-app-${app.id}`,
        vacancy_id: app.vacancy_id || v?.id || null,
        task_type: 'new_vacancy_outreach',
        engagement_score: stale ? 90 : 64,
        current_status: statusLine,
        rowHighlight: stale ? 'warning' : undefined,
        ownerRecruiterId: pickVacancyRecruiterId(v) ?? null,
        statusSinceIso: voorgesteld ? voorgesteld.toISOString() : (app.updated_at || app.created_at || null),
      });
    }
    out.sort((a, b) => {
      const aw = a.rowHighlight === 'warning' ? 0 : 1;
      const bw = b.rowHighlight === 'warning' ? 0 : 1;
      if (aw !== bw) return aw - bw;
      return (a.candidate_name || '').localeCompare(b.candidate_name || '', 'nl');
    });
    return out;
  }, [engagementApplications, user]);

  const fieldManagementTasks = useMemo((): EngagementTask[] => {
    const milestones = fieldMilestoneMonths.length ? fieldMilestoneMonths : DEFAULT_FIELD_MILESTONE_MONTHS;
    const milestoneSet = new Set(milestones);
    const rows: EngagementTask[] = [];
    for (const p of placements || []) {
      const st = (p.status || '').toLowerCase();
      if (st.includes('beeindig') || st.includes('afgerond') || st.includes('ended') || st.includes('gestopt')) continue;
      const activeLike =
        st.includes('actief') || st.includes('active') || st.includes('geplaatst') || st.includes('lopend') || !st;
      if (!activeLike) continue;
      if (p.end_date) {
        const endT = Date.parse(p.end_date);
        if (!Number.isNaN(endT) && endT < Date.now() - 86400000) continue;
      }
      const startIso = p.start_date || p.created_at;
      const months = fullCalendarMonthsSince(startIso);
      if (!milestoneSet.has(months)) continue;
      if (fieldMilestoneFilter !== 'all' && months !== fieldMilestoneFilter) continue;
      const cand = p.candidate;
      if (!cand?.id) continue;
      if (!hasCandidateEngagementConsent(cand)) continue;
      const vac = p.vacancy as Vacancy | undefined;
      const company =
        p.opdrachtgever?.name ||
        p.eindklant?.name ||
        vac?.client?.name ||
        vac?.eindklant?.name ||
        'opdrachtgever';
      const t = minimalTaskForContact(
        {
          id: cand.id,
          name: cand.name || 'Kandidaat',
          email: cand.email,
          phone: cand.phone,
          opted_out: false,
        },
        vac || null,
        user,
        {
          reason: `Fieldmanagement · ${months} maand(en) op opdracht · ${company}`,
          message: `Hoi ${(cand.name || '').split(/\s+/)[0] || ''},\n\nWe zijn nu zo’n ${months} maand onderweg bij ${company}. Hoe verloopt het, en is er iets dat we moeten bijsturen?\n\nGroet,\n${user.name || ''}`,
        }
      );
      rows.push({
        ...t,
        id: `field-mgmt-${p.id}-${months}`,
        vacancy_id: p.vacancy_id || vac?.id || null,
        placement_id: String(p.id).startsWith('app-') ? null : p.id,
        task_type: 'placement_followup',
        engagement_score: 70 + Math.min(18, months),
        current_status: `${months} mnd · ${company}`,
        ownerRecruiterId: pickVacancyRecruiterId(vac) ?? null,
        statusSinceIso: p.start_date || p.created_at || null,
      });
    }
    rows.sort((a, b) => (b.engagement_score || 0) - (a.engagement_score || 0));
    return rows;
  }, [placements, user, fieldMilestoneMonths, fieldMilestoneFilter]);

  const taskMatchesRecruiterFilter = useCallback(
    (task: EngagementTask) => {
      if (!filterRecruiterId) return true;
      const owned = task.ownerRecruiterId;
      if (owned != null && owned !== '') return owned === filterRecruiterId;
      let vid = task.vacancy_id as string | null | undefined;
      if (!vid && task.placement_id) {
        const pl = placements.find((x) => x.id === task.placement_id);
        vid = pl?.vacancy_id;
      }
      if (!vid) return false;
      const v = (vacancies || []).find((x) => x.id === vid) as any;
      return pickVacancyRecruiterId(v) === filterRecruiterId;
    },
    [filterRecruiterId, vacancies, placements]
  );

  const filterEngagementList = useCallback(
    (tasks: EngagementTask[]) =>
      tasks.filter((t) => !dismissedTaskIds.has(t.id)).filter((t) => taskMatchesRecruiterFilter(t)),
    [dismissedTaskIds, taskMatchesRecruiterFilter]
  );

  const todayTasksFiltered = useMemo(
    () => filterEngagementList(todayTasks),
    [todayTasks, filterEngagementList]
  );
  const proposedApplicationTasksFiltered = useMemo(
    () => filterEngagementList(proposedApplicationTasks),
    [proposedApplicationTasks, filterEngagementList]
  );

  const websiteWebhookInboxAppsAll = useMemo(() => {
    const rows = (engagementApplications || []).filter((app) => {
      if (!isWebsiteWebhookInboundApplication(app) || !isOpenSollicitatieStatus(app.status)) return false;
      const cand = app.candidates ?? app.candidate;
      const c = Array.isArray(cand) ? cand[0] : cand;
      if (!c?.id || !hasCandidateEngagementConsent(c)) return false;
      return true;
    });
    rows.sort((a, b) => Date.parse(String(b.created_at || 0)) - Date.parse(String(a.created_at || 0)));
    return rows;
  }, [engagementApplications]);

  const websiteWebhookInboxAppsFiltered = useMemo(() => {
    return websiteWebhookInboxAppsAll.filter((app) => {
      if (dismissedTaskIds.has(`wh-inbox-${app.id}`)) return false;
      if (!filterRecruiterId) return true;
      const vacRaw = app.vacancies ?? app.vacancy;
      const v = Array.isArray(vacRaw) ? vacRaw[0] : vacRaw;
      return pickVacancyRecruiterId(v) === filterRecruiterId;
    });
  }, [websiteWebhookInboxAppsAll, dismissedTaskIds, filterRecruiterId]);

  const websiteWebhookInboxAppsDisplay = useMemo(() => {
    const q = listSearch.trim().toLowerCase();
    if (!q) return websiteWebhookInboxAppsFiltered;
    return websiteWebhookInboxAppsFiltered.filter((app) => {
      const cand = app.candidates ?? app.candidate;
      const c = Array.isArray(cand) ? cand[0] : cand;
      const name = String(c?.name || '').toLowerCase();
      const vacRaw = app.vacancies ?? app.vacancy;
      const v = Array.isArray(vacRaw) ? vacRaw[0] : vacRaw;
      const vt = String(v?.title || '').toLowerCase();
      const src = websiteWebhookSourceDisplayLabel(app).toLowerCase();
      const st = String(app.status || '').toLowerCase();
      return name.includes(q) || vt.includes(q) || src.includes(q) || st.includes(q);
    });
  }, [websiteWebhookInboxAppsFiltered, listSearch]);

  const portalClientPreparedApps = useMemo(() => {
    return engagementApplications
      .filter((app) => {
        const p = parsePortalClientProposal(app.cv_snapshot);
        return !!(p?.published_at && String(p.published_at).trim());
      })
      .sort(
        (a, b) =>
          Date.parse(String(b.updated_at || b.created_at || 0)) -
          Date.parse(String(a.updated_at || a.created_at || 0))
      );
  }, [engagementApplications]);

  const availabilityUnknownFiltered = useMemo(
    () => filterEngagementList(availabilityUnknownTasks),
    [availabilityUnknownTasks, filterEngagementList]
  );
  const availabilityExpiringFiltered = useMemo(
    () => filterEngagementList(availabilityExpiringTasks),
    [availabilityExpiringTasks, filterEngagementList]
  );
  const availabilityUpcomingFiltered = useMemo(
    () => filterEngagementList(availabilityUpcomingMonthTasks),
    [availabilityUpcomingMonthTasks, filterEngagementList]
  );
  const endingEngagementTasksFiltered = useMemo(
    () => filterEngagementList(endingEngagementTasks),
    [endingEngagementTasks, filterEngagementList]
  );
  const fieldManagementTasksFiltered = useMemo(
    () => filterEngagementList(fieldManagementTasks),
    [fieldManagementTasks, filterEngagementList]
  );
  const dataConsentEngagementTasksFiltered = useMemo(
    () => filterEngagementList(dataConsentEngagementTasks),
    [dataConsentEngagementTasks, filterEngagementList]
  );
  const birthdayTasksFiltered = useMemo(
    () => filterEngagementList(birthdayTasks),
    [birthdayTasks, filterEngagementList]
  );

  const openCockpitSecondWindow = useCallback(() => {
    try {
      const url = `${window.location.origin}/app/candidate-engagement?cockpit=1`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!onCockpitLayoutMode) return;
    onCockpitLayoutMode(isCockpitWindow);
    return () => {
      onCockpitLayoutMode(false);
    };
  }, [isCockpitWindow, onCockpitLayoutMode]);

  useEffect(() => {
    if (!isCockpitWindow) return;
    const prev = document.title;
    document.title = 'Engagement · tweede scherm | Werftools';
    return () => {
      document.title = prev;
    };
  }, [isCockpitWindow]);

  useEffect(() => {
    let cancelled = false;
    const hadCache = !!(cachedPoolInit?.length && cachedPlacementsInit !== null);
    (async () => {
      if (!hadCache) {
        setDataLoading(true);
        setLoadProgress({ active: true, label: 'Talentpool & plaatsingen laden', percent: null });
      }
      try {
        const filterId =
          user.role === UserRole.CLIENT || user.role === UserRole.CLIENT_RECRUITER
            ? user.clientId
            : undefined;
        const [poolRes, plcData] = await Promise.all([
          supabase_get_paginated_talent_pool(filterId, 1, 280, '', 'updated_at', true),
          fetchLeanPlacementsForEngagement(user),
        ]);
        if (cancelled) return;
        const fetched = poolRes?.data || [];
        const merged = new Map<string, any>();
        for (const r of candidatesPropRef.current || []) merged.set(r.id || r.candidate?.id, r);
        for (const r of fetched) merged.set(r.id || r.candidate?.id, r);
        const mergedArr = Array.from(merged.values());
        setPoolRows(mergedArr);
        const placementsArr = Array.isArray(plcData) ? plcData : [];
        setPlacements(placementsArr);
        if (user?.id) {
          saveEngagementCache('pool', user, mergedArr);
          saveEngagementCache('placements', user, placementsArr);
        }
        if (!hadCache) {
          setLoadProgress((p) =>
            p.label === 'Talentpool & plaatsingen laden' ? { ...p, label: 'Talentpool gereed', percent: 100 } : p
          );
        }
      } catch (e) {
        console.error('[CandidateEngagementCenter] load failed', e);
        if (!hadCache) {
          const merged = new Map<string, any>();
          for (const r of candidatesPropRef.current || []) merged.set(r.id || r.candidate?.id, r);
          setPoolRows(Array.from(merged.values()));
        }
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user.clientId, user.role, user.id, cachedPoolInit, cachedPlacementsInit]);

  useEffect(() => {
    if (user?.id) setDismissedTaskIds(loadDismissedTaskIds(user.id));
  }, [user?.id]);

  useEffect(() => {
    if (!user.clientId || String(user.id || '').startsWith('impersonated-')) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.from('clients').select('data').eq('id', user.clientId).maybeSingle();
        if (cancelled || !data) return;
        const d = data.data as Record<string, unknown> | null | undefined;
        const raw = d?.field_management_milestones_months;
        if (Array.isArray(raw) && raw.length && raw.every((n) => typeof n === 'number')) {
          setFieldMilestoneMonths([...(raw as number[])].sort((a, b) => a - b));
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user.clientId, user.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('profiles').select('id,name,email,role').in('role', ['RECRUITER', 'ADMIN']).order('name');
      if (!cancelled && data) setRecruiters(data as { id: string; name: string | null; email: string | null; role: string }[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const hasCache = !!(cachedAppsInit && cachedAppsInit.length);
    // Defer until idle so the "Today" tab paints from pool/placements first.
    const deferTimer = window.setTimeout(() => {
      void runApps();
    }, hasCache ? 1200 : 60);

    async function runApps() {
      if (cancelled) return;
      if (!hasCache) {
        setEngagementAppsLoading(true);
        setLoadProgress({
          active: true,
          label: 'Sollicitaties tellen…',
          percent: null,
        });
      }
      try {
        const pageSize = 800;
        const hardCap = 12000;
        const portalFilter =
          user.role === UserRole.CLIENT || user.role === UserRole.CLIENT_RECRUITER ? user.clientId : null;

        let total: number | null = null;
        try {
          let countQ = supabase
            .from('applications')
            .select('id', { count: 'exact', head: true })
            .not('status', 'in', APPS_DB_EXCLUDE_STATUSES);
          if (portalFilter) {
            countQ = supabase
              .from('applications')
              .select('id, vacancies!inner(eindklant_id)', { count: 'exact', head: true })
              .not('status', 'in', APPS_DB_EXCLUDE_STATUSES)
              .eq('vacancies.eindklant_id', portalFilter);
          }
          const { count } = await countQ;
          if (typeof count === 'number') total = Math.min(count, hardCap);
        } catch {
          /* count is best-effort; fall back to indeterminate */
        }
        if (!cancelled && !hasCache) {
          setLoadProgress({
            active: true,
            label: 'Sollicitaties laden',
            percent: total != null ? 0 : null,
            detail: total != null ? `0 / ${total}` : undefined,
          });
        }

        let from = 0;
        const all: any[] = [];
        while (from < hardCap) {
          let q = supabase
            .from('applications')
            .select(APPLICATIONS_SELECT_LITE)
            .not('status', 'in', APPS_DB_EXCLUDE_STATUSES)
            .order('updated_at', { ascending: false })
            .range(from, from + pageSize - 1);
          if (portalFilter) {
            q = q.eq('vacancies.eindklant_id', portalFilter);
          }
          const { data, error } = await q;
          if (error) throw error;
          if (data?.length) {
            all.push(...data);
            if (!cancelled) {
              if (!hasCache) {
                setLoadProgress({
                  active: true,
                  label: 'Sollicitaties laden',
                  percent: total ? Math.min(100, Math.round((all.length / total) * 100)) : null,
                  detail: total ? `${all.length} / ${total}` : `${all.length} geladen`,
                });
              }
              // Stream rows: render them as they come in, even on first paint.
              setEngagementApplications([...all]);
            }
            if (data.length < pageSize) break;
            from += pageSize;
          } else {
            break;
          }
        }
        if (!cancelled) {
          setEngagementApplications(all);
          if (user?.id) saveEngagementCache('apps', user, all);
          if (!hasCache) {
            setLoadProgress({
              active: false,
              label: `Klaar · ${all.length} sollicitaties`,
              percent: 100,
              detail: undefined,
            });
          }
        }
      } catch (e) {
        console.error('[CandidateEngagementCenter] applications load failed', e);
        if (!cancelled && !hasCache) {
          setEngagementApplications([]);
          setLoadProgress({ active: false, label: 'Sollicitaties laden mislukt', percent: null });
        }
      } finally {
        if (!cancelled) setEngagementAppsLoading(false);
      }
    }

    return () => {
      cancelled = true;
      window.clearTimeout(deferTimer);
    };
  }, [user.role, user.clientId, user.id, cachedAppsInit]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  }, []);

  const dismissTask = useCallback(
    (taskId: string) => {
      if (!user?.id) return;
      setDismissedTaskIds((prev) => {
        const next = new Set(prev);
        next.add(taskId);
        try {
          localStorage.setItem(`wt_engagement_done_${user.id}`, JSON.stringify([...next]));
        } catch {
          /* ignore */
        }
        return next;
      });
      showToast('Taak afgevinkt (lokaal op dit apparaat)');
    },
    [user?.id, showToast]
  );

  const formatCommDate = useCallback((iso: string) => {
    try {
      return new Date(iso).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  }, []);

  const ensureCommsLoaded = useCallback(async (candidateId: string, force = false) => {
    if (!candidateId) return;
    if (!force && commsCacheRef.current[candidateId]) return;
    try {
      const rows = await supabase_get_candidate_communications(candidateId);
      const list = Array.isArray(rows) ? rows : [];
      setCommsCache((p) => ({ ...p, [candidateId]: list }));
      if (force) {
        setCommFocusIndex((p) => ({
          ...p,
          [candidateId]: list.length ? list.length - 1 : 0,
        }));
      }
    } catch (e) {
      console.warn('[CandidateEngagementCenter] load comms failed', e);
      setCommsCache((p) => ({ ...p, [candidateId]: [] }));
    }
  }, []);

  const toggleExpanded = useCallback(
    async (candidateId: string) => {
      const closing = expandedCandidateId === candidateId;
      if (closing) {
        setExpandedCandidateId(null);
        await releaseEngagementClaim(candidateId, user.id);
        setContactClaims((p) => ({ ...p, [candidateId]: null }));
        return;
      }
      setExpandedCandidateId(candidateId);
      setClaimBusyId(candidateId);
      const acq = await tryAcquireEngagementClaim(candidateId, user);
      setClaimBusyId(null);
      setContactClaims((p) => ({ ...p, [candidateId]: acq.claim }));
      if (acq.ok === false) {
        showToast(
          `Let op: ${acq.claim.claimed_by_name} heeft deze kandidaat geclaimd tot ${formatCommDate(acq.claim.claimed_until)}.`
        );
      }
      void fetchEngagementClaim(candidateId).then((cl) => {
        if (cl) setContactClaims((p) => ({ ...p, [candidateId]: cl }));
      });
      const c = getPoolCandidate(candidateId);
      const d = c ? parseCandidateAvailabilityDate(c) : null;
      setAvailabilityDateDraft((p) => {
        if (p[candidateId] !== undefined) return p;
        if (!d) return { ...p, [candidateId]: '' };
        return { ...p, [candidateId]: d.toISOString().slice(0, 10) };
      });
      await ensureCommsLoaded(candidateId, true);
    },
    [
      expandedCandidateId,
      user,
      ensureCommsLoaded,
      showToast,
      formatCommDate,
      getPoolCandidate,
    ]
  );

  useEffect(() => {
    const prev = prevExpandedRef.current;
    if (prev && prev !== expandedCandidateId) {
      void releaseEngagementClaim(prev, user.id);
    }
    prevExpandedRef.current = expandedCandidateId;
  }, [expandedCandidateId, user.id]);

  useEffect(() => {
    if (!expandedCandidateId) return;
    const t = window.setInterval(() => {
      void (async () => {
        const cl = await fetchEngagementClaim(expandedCandidateId);
        setContactClaims((p) => {
          if (cl) return { ...p, [expandedCandidateId]: cl };
          const prev = p[expandedCandidateId];
          if (prev?.claimed_until && new Date(prev.claimed_until) <= new Date()) {
            return { ...p, [expandedCandidateId]: null };
          }
          return p;
        });
      })();
    }, 20000);
    return () => window.clearInterval(t);
  }, [expandedCandidateId]);

  const assertContactClaim = useCallback(
    async (candidateId: string, candidateName: string) => {
      const acq = await tryAcquireEngagementClaim(candidateId, user);
      setContactClaims((p) => ({ ...p, [candidateId]: acq.claim }));
      if (acq.ok === false) {
        showToast(`${candidateName}: al in contact door ${acq.claim.claimed_by_name} (tot ${formatCommDate(acq.claim.claimed_until)})`);
        return false;
      }
      void fetchEngagementClaim(candidateId).then((cl) => {
        if (cl) setContactClaims((p) => ({ ...p, [candidateId]: cl }));
      });
      return true;
    },
    [user, showToast, formatCommDate]
  );

  const openWhatsAppForTask = useCallback(
    async (task: EngagementTask) => {
      if (task.opted_out || !task.consent_ok) return;
      setClaimBusyId(task.candidate_id);
      const ok = await assertContactClaim(task.candidate_id, task.candidate_name);
      setClaimBusyId(null);
      if (!ok) return;
      setActionTask(task);
      setWaModalOpen(true);
    },
    [assertContactClaim]
  );

  const openEmailForTask = useCallback(
    async (task: EngagementTask) => {
      if (task.opted_out || !task.consent_ok) return;
      setClaimBusyId(task.candidate_id);
      const ok = await assertContactClaim(task.candidate_id, task.candidate_name);
      setClaimBusyId(null);
      if (!ok) return;
      setActionTask(task);
      setEmailModalOpen(true);
    },
    [assertContactClaim]
  );

  const saveAvailabilityInline = useCallback(
    async (candidateId: string) => {
      const iso = (availabilityDateDraft[candidateId] || '').trim();
      if (!iso || !candidateId) {
        showToast('Kies een datum');
        return;
      }
      setAvailabilityDateSaving((p) => ({ ...p, [candidateId]: true }));
      try {
        const c = getPoolCandidate(candidateId);
        const nl = new Date(iso + 'T12:00:00').toLocaleDateString('nl-NL');
        const updatedAi = { ...(c?.ai_parsed_data || {}), available_from: iso };
        const { error } = await supabase.from('candidates').update({ availability: nl, ai_parsed_data: updatedAi }).eq('id', candidateId);
        if (error) throw error;
        showToast('Beschikbaarheid opgeslagen');
        setPoolRows((prev) =>
          prev.map((row) => {
            const cand = row?.candidate || row;
            if (cand?.id !== candidateId) return row;
            if (row.candidate) return { ...row, candidate: { ...cand, availability: nl, ai_parsed_data: updatedAi } };
            return { ...cand, availability: nl, ai_parsed_data: updatedAi };
          })
        );
        onRefreshPool?.();
      } catch (e) {
        console.error(e);
        showToast('Opslaan beschikbaarheid mislukt');
      } finally {
        setAvailabilityDateSaving((p) => ({ ...p, [candidateId]: false }));
      }
    },
    [availabilityDateDraft, getPoolCandidate, showToast, onRefreshPool]
  );

  const saveInlineNote = useCallback(
    async (candidateId: string) => {
      const text = (inlineNotesDraft[candidateId] || '').trim();
      if (!text || !candidateId) return;
      setInlineNotesSaving((p) => ({ ...p, [candidateId]: true }));
      try {
        const { error } = await supabase.from('candidate_notes').insert({
          candidate_id: candidateId,
          author_id: user.id,
          author_name: user.name || 'Onbekend',
          content: text,
        });
        if (error) throw error;
        showToast('Opgeslagen in notities');
        setInlineNotesDraft((p) => ({ ...p, [candidateId]: '' }));
        // Refresh comms panel if it's open for this candidate
        setCommsCache((p) => ({ ...p, [candidateId]: undefined as any }));
        void ensureCommsLoaded(candidateId, true);
      } catch (e) {
        console.error(e);
        showToast('Opslaan notitie mislukt');
      } finally {
        setInlineNotesSaving((p) => ({ ...p, [candidateId]: false }));
      }
    },
    [inlineNotesDraft, user.id, user.name, showToast, ensureCommsLoaded]
  );

  const saveQuickLog = useCallback(
    async (candidateId: string) => {
      const d = quickLogDraft[candidateId];
      const text = (d?.text || '').trim();
      if (!candidateId || !text) return;
      try {
        if (d.type === 'note') {
          const { error } = await supabase.from('candidate_notes').insert({
            candidate_id: candidateId,
            author_id: user.id,
            author_name: user.name || 'Onbekend',
            content: text,
          });
          if (error) throw error;
        } else {
          // Lightweight log as comm entry (no Bird / no inbox button)
          const { error } = await supabase.from('candidate_communications').insert({
            candidate_id: candidateId,
            user_id: user.id,
            type: d.type,
            content: text,
          });
          if (error) throw error;
        }
        setQuickLogDraft((p) => ({ ...p, [candidateId]: { ...d, text: '' } }));
        setCommsCache((p) => ({ ...p, [candidateId]: undefined as any }));
        await ensureCommsLoaded(candidateId, true);
        showToast('Opgeslagen');
      } catch (e) {
        console.error(e);
        showToast('Opslaan mislukt');
      }
    },
    [quickLogDraft, user.id, user.name, ensureCommsLoaded, showToast]
  );

  const startCallForTask = useCallback(
    async (task: EngagementTask) => {
      if (!onStartCall) return;
      setClaimBusyId(task.candidate_id);
      const ok = await assertContactClaim(task.candidate_id, task.candidate_name);
      setClaimBusyId(null);
      if (!ok) return;
      const row = poolRows.find((r) => (r.candidate?.id || r.id) === task.candidate_id);
      const candidate = row?.candidate || {
        id: task.candidate_id,
        name: task.candidate_name,
        email: task.candidate_email,
        phone: task.candidate_phone,
      };
      onStartCall({ id: task.candidate_id, candidate });
    },
    [onStartCall, poolRows, assertContactClaim]
  );

  const applicationToWebhookInboxTask = useCallback(
    (app: any): EngagementTask | null => {
      if (!isWebsiteWebhookInboundApplication(app) || !isOpenSollicitatieStatus(app.status)) return null;
      const cand = app.candidates ?? app.candidate;
      const c = Array.isArray(cand) ? cand[0] : cand;
      if (!c?.id || !hasCandidateEngagementConsent(c)) return null;
      const vacRaw = app.vacancies ?? app.vacancy;
      const v = Array.isArray(vacRaw) ? vacRaw[0] : vacRaw;
      const vacObj = v
        ? ({
            id: v.id,
            clientId: v.client_id,
            title: v.title,
            status: v.status,
            data: v.data || {},
            createdAt: v.created_at,
            updatedAt: v.updated_at,
            description: '',
            responsibilities: {},
          } as Vacancy)
        : null;
      const t = minimalTaskForContact(
        { id: c.id, name: c.name, email: c.email, phone: c.phone, opted_out: false },
        vacObj,
        user,
        {
          reason: `Website · ${v?.title || 'Vacature'}`,
          message: `Hoi ${(c.name || '').split(/\s+/)[0] || ''},\n\nIk reageer op je sollicitatie via onze website.\n\nGroet,\n${user.name || ''}`,
        }
      );
      return {
        ...t,
        id: `wh-inbox-${app.id}`,
        vacancy_id: app.vacancy_id || v?.id || null,
        task_type: 'new_vacancy_outreach',
        engagement_score: 62,
        current_status: app.status || '—',
        ownerRecruiterId: pickVacancyRecruiterId(v) ?? null,
        statusSinceIso: app.created_at || app.updated_at || null,
      };
    },
    [user]
  );

  const refreshLive = useCallback(async () => {
    onRefreshPool?.();
    setDataLoading(true);
    setEngagementAppsLoading(true);
    setLoadProgress({ active: true, label: 'Vernieuwen · talentpool & plaatsingen', percent: null });
    try {
      const filterId =
        user.role === UserRole.CLIENT || user.role === UserRole.CLIENT_RECRUITER
          ? user.clientId
          : undefined;
      const poolRes = await supabase_get_paginated_talent_pool(filterId, 1, 280, '', 'updated_at', true);
      const plcData = await fetchLeanPlacementsForEngagement(user);
      const fetched = poolRes?.data || [];
      const merged = new Map<string, any>();
      for (const r of candidatesPropRef.current || []) merged.set(r.id || r.candidate?.id, r);
      for (const r of fetched) merged.set(r.id || r.candidate?.id, r);
      const mergedArr = Array.from(merged.values());
      setPoolRows(mergedArr);
      const placementsArr = Array.isArray(plcData) ? plcData : [];
      setPlacements(placementsArr);
      if (user?.id) {
        saveEngagementCache('pool', user, mergedArr);
        saveEngagementCache('placements', user, placementsArr);
      }

      const pageSize = 800;
      const hardCap = 12000;
      const portalFilter =
        user.role === UserRole.CLIENT || user.role === UserRole.CLIENT_RECRUITER ? user.clientId : null;

      let total: number | null = null;
      try {
        let countQ = supabase
          .from('applications')
          .select('id', { count: 'exact', head: true })
          .not('status', 'in', APPS_DB_EXCLUDE_STATUSES);
        if (portalFilter) {
          countQ = supabase
            .from('applications')
            .select('id, vacancies!inner(eindklant_id)', { count: 'exact', head: true })
            .not('status', 'in', APPS_DB_EXCLUDE_STATUSES)
            .eq('vacancies.eindklant_id', portalFilter);
        }
        const { count } = await countQ;
        if (typeof count === 'number') total = Math.min(count, hardCap);
      } catch {
        /* best-effort */
      }
      setLoadProgress({
        active: true,
        label: 'Sollicitaties laden',
        percent: total != null ? 0 : null,
        detail: total != null ? `0 / ${total}` : undefined,
      });

      let from = 0;
      const allApps: any[] = [];
      while (from < hardCap) {
        let q = supabase
          .from('applications')
          .select(APPLICATIONS_SELECT_LITE)
          .not('status', 'in', APPS_DB_EXCLUDE_STATUSES)
          .order('updated_at', { ascending: false })
          .range(from, from + pageSize - 1);
        if (portalFilter) {
          q = q.eq('vacancies.eindklant_id', portalFilter);
        }
        const { data, error } = await q;
        if (error) throw error;
        if (data?.length) {
          allApps.push(...data);
          setLoadProgress({
            active: true,
            label: 'Sollicitaties laden',
            percent: total ? Math.min(100, Math.round((allApps.length / total) * 100)) : null,
            detail: total ? `${allApps.length} / ${total}` : `${allApps.length} geladen`,
          });
          // Stream rows so the table fills up as pages arrive.
          setEngagementApplications([...allApps]);
          if (data.length < pageSize) break;
          from += pageSize;
        } else {
          break;
        }
      }
      setEngagementApplications(allApps);
      if (user?.id) saveEngagementCache('apps', user, allApps);
      setLoadProgress({
        active: false,
        label: `Klaar · ${allApps.length} sollicitaties`,
        percent: 100,
        detail: undefined,
      });

      showToast('Data vernieuwd');
    } catch (e) {
      console.error(e);
      showToast('Vernieuwen mislukt');
      setLoadProgress({ active: false, label: 'Vernieuwen mislukt', percent: null });
    } finally {
      setDataLoading(false);
      setEngagementAppsLoading(false);
    }
  }, [user.clientId, user.role, onRefreshPool, showToast]);

  const kpis = useMemo(
    () => [
      {
        label: 'Te benaderen vandaag',
        value: String(kpiStats.todayCount),
        icon: Users,
        hint: dataLoading ? 'Laden…' : 'Uit jouw pool & plaatsingen',
      },
      {
        label: 'Hoge prioriteit matches',
        value: String(kpiStats.highMatch),
        icon: Target,
        hint: dataLoading ? '…' : 'Prioriteit hoog/kritiek',
      },
      {
        label: 'Warm · follow-up due',
        value: String(kpiStats.warmFollowup),
        icon: Flame,
        hint: '> 60 dagen geen update',
      },
      {
        label: 'Lopende opdrachten eindigen',
        value: String(kpiStats.endingSoon),
        icon: CalendarClock,
        hint: '< 30 dagen tot einde',
      },
      {
        label: 'Gem. responsrate',
        value: '—',
        icon: Percent,
        hint: 'Coming soon',
      },
      {
        label: 'Plaatsingen check-in',
        value: String(kpiStats.placementRisk),
        icon: AlertTriangle,
        hint: 'Follow-up uit queue',
      },
    ],
    [kpiStats, dataLoading]
  );

  const LoadingBar = ({ compact = false }: { compact?: boolean }) => {
    const visible = loadProgress.active || loadProgress.percent === 100;
    if (!visible) return null;
    const pct = loadProgress.percent;
    const indeterminate = pct === null;
    return (
      <div
        className={`rounded-xl border border-slate-200 bg-white shadow-sm ${
          compact ? 'px-3 py-2' : 'px-4 py-3'
        }`}
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <div className="flex items-center gap-2 min-w-0">
            <Loader2
              size={14}
              className={`shrink-0 ${loadProgress.active ? 'animate-spin text-indigo-600' : 'text-emerald-600'}`}
            />
            <span className={`font-semibold text-slate-800 truncate ${compact ? 'text-[11px]' : 'text-[12px]'}`}>
              {loadProgress.label || 'Laden…'}
            </span>
          </div>
          <span className="text-[11px] tabular-nums text-slate-500 shrink-0">
            {indeterminate ? (loadProgress.detail || '…') : `${pct}%${loadProgress.detail ? ` · ${loadProgress.detail}` : ''}`}
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
          {indeterminate ? (
            <div className="h-full w-1/3 rounded-full bg-indigo-500/80 wt-loading-stripe-anim" />
          ) : (
            <div
              className={`h-full rounded-full transition-[width] duration-300 ease-out ${
                pct === 100 ? 'bg-emerald-500' : 'bg-indigo-500'
              }`}
              style={{ width: `${Math.max(2, pct ?? 0)}%` }}
            />
          )}
        </div>
        <style>{`
          @keyframes wtLoadingStripe {
            0% { transform: translateX(-110%); }
            100% { transform: translateX(330%); }
          }
          .wt-loading-stripe-anim {
            animation: wtLoadingStripe 1.4s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  };

  const TabButton = ({ id, label, badge }: { id: MainTab; label: string; badge?: number }) => (
    <button
      type="button"
      onClick={() => {
        setMainTab(id);
        setListSearch('');
        if (id !== 'open_application') setWebhookInboxExpandedAppId(null);
      }}
      className={`shrink-0 rounded-md border px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${
        mainTab === id
          ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
          : 'border-slate-200/90 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
      }`}
    >
      <span className="inline-flex items-center gap-2">
        {label}
        {typeof badge === 'number' && badge > 0 ? (
          <span className="min-w-[1.25rem] rounded-full bg-amber-500 px-1.5 py-0 text-[10px] font-bold text-white tabular-nums shadow-sm">
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </span>
    </button>
  );

  /** Compact contact + log shortcuts in table rows (stops row click propagation). */
  const ContactQuickActions = ({ task }: { task: EngagementTask }) => {
    const claim = contactClaims[task.candidate_id];
    const blockedByOther = !!(claim && claim.claimed_by_user_id !== user.id);
    const blocked = task.opted_out || !task.consent_ok || blockedByOther;
    const busyHere = claimBusyId === task.candidate_id;
    return (
      <div className="flex items-center justify-end gap-1 flex-nowrap" onClick={(e) => e.stopPropagation()}>
        {busyHere && <Loader2 size={14} className="animate-spin text-slate-400 mr-1" />}
        <button
          type="button"
          title={
            blockedByOther
              ? `Geclaimd door ${claim?.claimed_by_name || 'collega'}`
              : 'WhatsApp'
          }
          disabled={blocked || !task.candidate_phone || busyHere}
          onClick={() => void openWhatsAppForTask(task)}
          className="p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-35 shadow-sm transition-colors"
        >
          <MessageCircle size={15} strokeWidth={2.25} />
        </button>
        <button
          type="button"
          title={blockedByOther ? `Geclaimd door ${claim?.claimed_by_name || 'collega'}` : 'E-mail'}
          disabled={blocked || !task.candidate_email || busyHere}
          onClick={() => void openEmailForTask(task)}
          className="p-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-35 shadow-sm"
        >
          <Mail size={15} strokeWidth={2.25} />
        </button>
        <button
          type="button"
          title={blockedByOther ? `Geclaimd door ${claim?.claimed_by_name || 'collega'}` : 'Bellen'}
          disabled={blocked || !onStartCall || busyHere}
          onClick={() => void startCallForTask(task)}
          className="p-2 rounded-lg border border-slate-200 bg-white text-blue-700 hover:bg-blue-50 disabled:opacity-35"
        >
          <Phone size={15} strokeWidth={2.25} />
        </button>
      </div>
    );
  };

  const engagementTaskRowNodes = useCallback(
    (row: EngagementTask) => {
      const cid = row.candidate_id;
      const expanded = expandedCandidateId === cid;
      const comms = commsCache[cid];
      const noteDraft = inlineNotesDraft[cid] ?? '';
      const draft = quickLogDraft[cid] || { type: 'note' as const, text: '' };
      const saving = !!inlineNotesSaving[cid];
      const claim = contactClaims[cid];
      const blockedByOther = !!(claim && claim.claimed_by_user_id !== user.id);
      const ownClaim = !!(claim && claim.claimed_by_user_id === user.id);
      const sortedComms = Array.isArray(comms)
        ? [...comms].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          )
        : [];
      const maxIdx = Math.max(0, sortedComms.length - 1);
      const idx = Math.min(Math.max(0, commFocusIndex[cid] ?? maxIdx), maxIdx);
      const flowItem = sortedComms[idx];
      const dateSaving = !!availabilityDateSaving[cid];

      return (
        <React.Fragment key={row.id}>
          <tr
            className={`hover:bg-slate-50/80 transition-colors ${
              detailModal?.candidateId === cid ? 'bg-indigo-50/60' : ''
            } ${row.rowHighlight === 'warning' ? 'bg-amber-50/75' : ''}`}
          >
            <td className="px-2 py-2.5 align-middle">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(cid);
                }}
                className={`p-1.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-transform ${
                  expanded ? 'rotate-180' : ''
                }`}
                title={expanded ? 'Inklappen' : 'Uitklappen'}
              >
                <ChevronDown size={14} />
              </button>
            </td>
            <td
              className="px-3 py-2.5 font-semibold text-slate-900 cursor-pointer"
              onClick={() =>
                setDetailModal({
                  candidateId: cid,
                  vacancyId: row.vacancy_id || undefined,
                })
              }
            >
              {row.candidate_name}
            </td>
            <td
              className="px-3 py-2.5 text-slate-600 hidden md:table-cell max-w-[160px] truncate cursor-pointer"
              title={row.current_status}
              onClick={() => setDetailModal({ candidateId: cid, vacancyId: row.vacancy_id || undefined })}
            >
              {row.current_status}
            </td>
            <td
              className="px-3 py-2.5 text-slate-700 cursor-pointer"
              onClick={() => setDetailModal({ candidateId: cid, vacancyId: row.vacancy_id || undefined })}
            >
              {row.reason}
            </td>
            <td
              className="px-3 py-2.5 text-slate-700 tabular-nums whitespace-nowrap"
              title={row.statusSinceIso ? `Sinds ${new Date(row.statusSinceIso).toLocaleString('nl-NL')}` : 'Onbekend sinds'}
            >
              {formatDurationSinceNl(row.statusSinceIso)}
            </td>
            <td className="px-3 py-2.5">
              <div className="flex items-start gap-2">
                <textarea
                  value={noteDraft}
                  onChange={(e) => setInlineNotesDraft((p) => ({ ...p, [cid]: e.target.value }))}
                  rows={2}
                  placeholder="Inline notitie… (blijft staan tot je opslaat)"
                  className="w-full text-[12px] border border-slate-200 rounded-lg px-2.5 py-2 resize-y min-h-[46px]"
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void saveInlineNote(cid);
                    }}
                    disabled={saving || !noteDraft.trim()}
                    className="inline-flex items-center justify-center gap-1 px-2.5 py-2 rounded-lg border border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-800 hover:bg-slate-100 disabled:opacity-40"
                    title="Opslaan in notities"
                  >
                    <Save size={14} /> Save
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissTask(row.id);
                    }}
                    className="inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-[11px] font-semibold text-emerald-900 hover:bg-emerald-100"
                    title="Uit dit overzicht halen (alleen bij jou lokaal)"
                  >
                    <CheckCircle2 size={14} /> Klaar
                  </button>
                </div>
              </div>
            </td>
            <td className="px-2 py-2 align-middle">
              <ContactQuickActions task={row} />
            </td>
          </tr>

          {expanded && (
            <tr className="bg-slate-50/30">
              <td colSpan={7} className="px-3 py-3">
                <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
                  {ownClaim && !blockedByOther && (
                    <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-950">
                      <Lock size={14} className="shrink-0 mt-0.5 text-emerald-700" />
                      <span>
                        <span className="font-semibold">Contactclaim actief</span> — jij bent gekoppeld tot{' '}
                        {claim?.claimed_until ? formatCommDate(claim.claimed_until) : '—'}. Collega&apos;s zien dat jij deze kandidaat benadert;
                        zij krijgen daarna een waarschuwing als zij ook contact zoeken.
                      </span>
                    </div>
                  )}
                  {blockedByOther && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-950">
                      <Lock size={14} className="shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold">{claim?.claimed_by_name}</span> is deze kandidaat aan het benaderen (claim tot{' '}
                        {claim?.claimed_until ? formatCommDate(claim.claimed_until) : '—'}). Jij kunt nog wel notities lezen; contactknoppen
                        zijn geblokkeerd.
                      </span>
                    </div>
                  )}
                  {mainTab === 'availability' && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 px-3 py-2">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-900 mb-1">
                        Beschikbaar vanaf (inline)
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          type="date"
                          value={availabilityDateDraft[cid] ?? ''}
                          onChange={(e) =>
                            setAvailabilityDateDraft((p) => ({ ...p, [cid]: e.target.value }))
                          }
                          className="text-[12px] border border-emerald-200 rounded-lg px-2 py-1.5 bg-white"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void saveAvailabilityInline(cid);
                          }}
                          disabled={dateSaving || !(availabilityDateDraft[cid] || '').trim()}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-[11px] font-semibold hover:bg-emerald-800 disabled:opacity-40"
                        >
                          {dateSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                          Opslaan
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="grid gap-3 lg:grid-cols-2">
                  <div>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                        Communicatie & notities (tijdlijn)
                      </div>
                      {sortedComms.length > 0 && (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            title="Vorige (ouder)"
                            disabled={idx <= 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCommFocusIndex((p) => ({ ...p, [cid]: Math.max(0, idx - 1) }));
                            }}
                            className="p-1 rounded border border-slate-200 bg-white disabled:opacity-30"
                          >
                            <ChevronLeft size={14} />
                          </button>
                          <span className="text-[10px] text-slate-500 tabular-nums min-w-[4.5rem] text-center">
                            {idx + 1} / {sortedComms.length}
                          </span>
                          <button
                            type="button"
                            title="Volgende (nieuwer)"
                            disabled={idx >= maxIdx}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCommFocusIndex((p) => ({ ...p, [cid]: Math.min(maxIdx, idx + 1) }));
                            }}
                            className="p-1 rounded border border-slate-200 bg-white disabled:opacity-30"
                          >
                            <ChevronRight size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                    {!Array.isArray(comms) && <div className="text-[12px] text-slate-500">Laden…</div>}
                    {Array.isArray(comms) && comms.length === 0 && (
                      <div className="text-[12px] text-slate-500">Nog geen communicatie of notities.</div>
                    )}
                    {flowItem && (
                      <div className="text-[12px] text-slate-700">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <span className="text-[11px] font-semibold text-slate-800">
                            {String(flowItem.type || 'bericht').toUpperCase()} · {flowItem.profiles?.name || '—'}
                          </span>
                          <span className="text-[10px] text-slate-500 whitespace-nowrap">
                            {formatCommDate(flowItem.created_at)}
                          </span>
                        </div>
                        <pre className="mt-2 whitespace-pre-wrap text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2 max-h-[200px] overflow-auto">
                          {String(flowItem.content || '')}
                        </pre>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">
                      Snel loggen (notitie/bericht)
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <select
                        value={draft.type}
                        onChange={(e) =>
                          setQuickLogDraft((p) => ({
                            ...p,
                            [cid]: { ...(p[cid] || draft), type: e.target.value as 'note' | 'whatsapp' | 'email' | 'phone' },
                          }))
                        }
                        className="text-[12px] font-medium border border-slate-200 rounded-lg px-3 py-2 bg-white"
                      >
                        <option value="note">Notitie</option>
                        <option value="whatsapp">WhatsApp (log)</option>
                        <option value="email">E-mail (log)</option>
                        <option value="phone">Telefoon (log)</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => void saveQuickLog(cid)}
                        disabled={!draft.text.trim()}
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-800 disabled:opacity-40"
                      >
                        <Save size={14} /> Opslaan
                      </button>
                    </div>
                    <textarea
                      value={draft.text}
                      onChange={(e) =>
                        setQuickLogDraft((p) => ({
                          ...p,
                          [cid]: { ...(p[cid] || draft), text: e.target.value },
                        }))
                      }
                      rows={4}
                      placeholder="Typ hier een notitie of korte samenvatting…"
                      className="w-full text-[12px] border border-slate-200 rounded-lg px-3 py-2 resize-y min-h-[96px]"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">
                      Notitie wordt opgeslagen in <span className="font-semibold">Notities</span>; logs komen in communicatie-tijdlijn.
                    </p>
                  </div>
                  </div>
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    },
    [
      expandedCandidateId,
      commsCache,
      inlineNotesDraft,
      quickLogDraft,
      inlineNotesSaving,
      detailModal?.candidateId,
      toggleExpanded,
      saveInlineNote,
      saveQuickLog,
      contactClaims,
      user.id,
      commFocusIndex,
      mainTab,
      availabilityDateDraft,
      availabilityDateSaving,
      formatCommDate,
      saveAvailabilityInline,
      dismissTask,
    ]
  );

  const renderEngagementTasksTableShell = ({
    title,
    subtitle,
    tasks,
    loading,
    emptyLabelWhenLoaded,
    showFilterSpacer,
  }: {
    title: string;
    subtitle?: string;
    tasks: EngagementTask[];
    loading: boolean;
    emptyLabelWhenLoaded: string;
    showFilterSpacer?: boolean;
  }) => {
    const q = listSearch.trim().toLowerCase();
    const filteredTasks = q
      ? tasks.filter((t) => {
          const hay = [
            t.candidate_name,
            t.candidate_email,
            t.candidate_phone,
            t.current_status,
            t.reason,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return hay.includes(q);
        })
      : tasks;
    return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/80">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Bot size={16} className="text-indigo-600" />
              <span className="text-[12px] font-bold text-slate-800">{title}</span>
              <span className="text-[10px] font-semibold text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5">
                {q ? `${filteredTasks.length} / ${tasks.length}` : `${tasks.length}`}
              </span>
            </div>
            {subtitle ? (
              <p className="text-[10px] text-slate-500 pl-7 max-w-2xl">{subtitle}</p>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search
                size={13}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                placeholder="Zoek naam, e-mail, telefoon, status…"
                className="pl-7 pr-7 py-1.5 w-[260px] text-[12px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
              {listSearch && (
                <button
                  type="button"
                  onClick={() => setListSearch('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  title="Wissen"
                >
                  <X size={13} />
                </button>
              )}
            </div>
            {showFilterSpacer ? (
              <button type="button" className="text-[11px] font-medium text-slate-500 hover:text-slate-800 flex items-center gap-1">
                <Filter size={12} /> Filters
              </button>
            ) : null}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[12px]">
          <thead>
            <tr className="border-b border-slate-100 bg-white text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              <th className="px-2 py-2.5 w-[44px]" />
              <th className="px-3 py-2.5">Kandidaat</th>
              <th className="px-3 py-2.5 hidden md:table-cell">Status</th>
              <th className="px-3 py-2.5 min-w-[220px]">Onderwerp</th>
              <th className="px-3 py-2.5 w-[100px] whitespace-nowrap">Op status</th>
              <th className="px-3 py-2.5 min-w-[240px]">Inline notitie</th>
              <th className="px-2 py-2.5 text-right min-w-[160px]">Contact</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-400 text-[11px]">
                  Laden… zie de balk bovenaan voor voortgang.
                </td>
              </tr>
            )}
            {!loading && tasks.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-500 text-[12px]">
                  {emptyLabelWhenLoaded}
                </td>
              </tr>
            )}
            {!loading && tasks.length > 0 && filteredTasks.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-500 text-[12px]">
                  Geen resultaten voor &ldquo;{listSearch}&rdquo;.{' '}
                  <button
                    type="button"
                    onClick={() => setListSearch('')}
                    className="text-indigo-600 hover:underline font-semibold"
                  >
                    Wis zoekopdracht
                  </button>
                </td>
              </tr>
            )}
            {!loading && filteredTasks.map((row) => engagementTaskRowNodes(row))}
          </tbody>
        </table>
      </div>
    </div>
    );
  };

  const renderTodayActions = () =>
    renderEngagementTasksTableShell({
      title: 'Acties vandaag · AI-wachtrij',
      subtitle:
        'WhatsApp, e-mail en bellen. Klik het pijltje voor recente communicatie en snelle logging. Met “Klaar” verberg je een rij lokaal op dit apparaat.',
      tasks: todayTasksFiltered,
      loading: dataLoading,
      emptyLabelWhenLoaded:
        'Geen engagement-taken gevonden op basis van je huidige data. Vernieuw of voeg kandidaten/plaatsingen toe.',
      showFilterSpacer: true,
    });

  const renderOpenApplications = () => {
    const loading = dataLoading || engagementAppsLoading;
    const baseCount = websiteWebhookInboxAppsFiltered.length;
    const disp = websiteWebhookInboxAppsDisplay;
    const q = listSearch.trim().toLowerCase();

    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50/90 via-white to-slate-50/80 p-4 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 max-w-3xl space-y-2">
              <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
                <Zap className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                Inbox · website en webhook
              </h3>
              <p className="text-[11px] leading-relaxed text-slate-600">
                Alleen sollicitaties die via jullie externe apply-webhook binnenkomen — niet de algemene shortlist
                vanuit recruiters. Nieuw ingestuurde kandidaten krijgen voortaan een herkenbare markering in de data;
                bestaande rijen tonen mee zodra ze al die markering hebben.
              </p>
              <p className="border-l-2 border-emerald-300 pl-2 text-[10px] italic text-slate-500">
                <span className="font-semibold not-italic text-slate-600">Ontwerp:</span> behandel dit als vaste
                &ldquo;bron-todo&rdquo;: plan 2 korte blocks per dag, gebruik het tab-badge als signaal, en overweeg later
                een compacte zijbalk of dock zodat dit overzicht naast Pipeline blijft staan.
              </p>
            </div>
            <span className="shrink-0 rounded-full bg-white px-3 py-1 text-xs font-semibold text-emerald-900 ring-1 ring-emerald-200">
              {q ? `${disp.length} / ${baseCount}` : baseCount} open
            </span>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
            <div className="relative min-w-[200px] flex-1 max-w-md">
              <Search
                size={13}
                className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                placeholder="Zoek op naam, vacature, bron…"
                className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-7 pr-7 text-[12px] focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
              {listSearch ? (
                <button
                  type="button"
                  title="Wissen"
                  onClick={() => setListSearch('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                >
                  <X size={13} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-[12px]">
              <thead>
                <tr className="border-b border-slate-100 bg-white text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  <th className="w-[88px] px-2 py-2.5">Open</th>
                  <th className="px-3 py-2.5">Kandidaat</th>
                  <th className="hidden px-3 py-2.5 sm:table-cell">Bron</th>
                  <th className="px-3 py-2.5">Vacature</th>
                  <th className="whitespace-nowrap px-3 py-2.5">Sollicitatie</th>
                  <th className="hidden px-3 py-2.5 md:table-cell">Status</th>
                  <th className="min-w-[120px] px-2 py-2.5 text-right">Klaar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-slate-400">
                      Laden…
                    </td>
                  </tr>
                )}
                {!loading && disp.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-[12px] text-slate-500">
                      Geen website/webhook-sollicitaties in deze scope, of er is nog geen markering op oudere data.
                      Controleer het vacature-eigenaar-filter of vernieuw de lijst. Nieuwe aanmeldingen via de
                      apply-webhook verschijnen hier automatisch.
                    </td>
                  </tr>
                )}
                {!loading &&
                  disp.map((app) => {
                    const cand = app.candidates ?? app.candidate;
                    const c = Array.isArray(cand) ? cand[0] : cand;
                    const vacRaw = app.vacancies ?? app.vacancy;
                    const v = Array.isArray(vacRaw) ? vacRaw[0] : vacRaw;
                    const cid = c?.id as string;
                    const vid = (app.vacancy_id || v?.id) as string | undefined;
                    const expanded = webhookInboxExpandedAppId === app.id;
                    const task = applicationToWebhookInboxTask(app);
                    const solIso = app.created_at || app.updated_at;
                    return (
                      <React.Fragment key={app.id}>
                        <tr
                          className={`transition-colors hover:bg-slate-50/80 ${
                            detailModal?.candidateId === cid ? 'bg-indigo-50/50' : ''
                          }`}
                        >
                          <td className="px-2 py-2.5 align-middle">
                            <button
                              type="button"
                              onClick={() => setWebhookInboxExpandedAppId(expanded ? null : app.id)}
                              className={`rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition-colors ${
                                expanded
                                  ? 'border-emerald-600 bg-emerald-600 text-white'
                                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              {expanded ? 'Sluit' : 'Open'}
                            </button>
                          </td>
                          <td
                            className="cursor-pointer px-3 py-2.5 font-semibold text-slate-900"
                            onClick={() =>
                              setDetailModal({
                                candidateId: cid,
                                vacancyId: vid,
                                initialTab: 'overview',
                              })
                            }
                          >
                            {c?.name || '—'}
                          </td>
                          <td
                            className="hidden max-w-[160px] cursor-pointer truncate px-3 py-2.5 text-slate-600 sm:table-cell"
                            title={websiteWebhookSourceDisplayLabel(app)}
                            onClick={() =>
                              setDetailModal({
                                candidateId: cid,
                                vacancyId: vid,
                                initialTab: 'overview',
                              })
                            }
                          >
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700 ring-1 ring-slate-200">
                              {websiteWebhookSourceDisplayLabel(app)}
                            </span>
                          </td>
                          <td
                            className="cursor-pointer px-3 py-2.5 text-slate-700"
                            onClick={() =>
                              setDetailModal({
                                candidateId: cid,
                                vacancyId: vid,
                                initialTab: 'vacatureflow',
                              })
                            }
                          >
                            {v?.title || '—'}
                          </td>
                          <td
                            className="cursor-pointer whitespace-nowrap px-3 py-2.5 text-slate-600 tabular-nums"
                            title={solIso ? new Date(solIso).toLocaleString('nl-NL') : ''}
                            onClick={() =>
                              setDetailModal({
                                candidateId: cid,
                                vacancyId: vid,
                                initialTab: 'overview',
                              })
                            }
                          >
                            {formatRelativeTimeNl(solIso)}
                          </td>
                          <td
                            className="hidden cursor-pointer px-3 py-2.5 text-slate-500 md:table-cell"
                            onClick={() =>
                              setDetailModal({
                                candidateId: cid,
                                vacancyId: vid,
                                initialTab: 'overview',
                              })
                            }
                          >
                            {app.status || '—'}
                          </td>
                          <td className="px-2 py-2.5 text-right align-middle">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                dismissTask(`wh-inbox-${app.id}`);
                              }}
                              className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-900 hover:bg-emerald-100"
                              title="Uit dit overzicht (lokaal op dit apparaat)"
                            >
                              <CheckCircle2 size={14} /> Klaar
                            </button>
                          </td>
                        </tr>
                        {expanded && task && cid ? (
                          <tr className="bg-emerald-50/40">
                            <td colSpan={7} className="px-4 py-3">
                              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setDetailModal({
                                        candidateId: cid,
                                        vacancyId: vid,
                                        initialTab: 'documents',
                                      });
                                      setWebhookInboxExpandedAppId(null);
                                    }}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
                                  >
                                    <FileText size={14} className="text-amber-600" />
                                    CV mappen
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setDetailModal({
                                        candidateId: cid,
                                        vacancyId: vid,
                                        initialTab: 'ai_analysis',
                                      });
                                      setWebhookInboxExpandedAppId(null);
                                    }}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
                                  >
                                    <BrainCircuit size={14} className="text-indigo-600" />
                                    {'Profiel & match'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setDetailModal({
                                        candidateId: cid,
                                        vacancyId: vid,
                                        initialTab: 'comm',
                                      });
                                      setWebhookInboxExpandedAppId(null);
                                    }}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
                                  >
                                    <MessageCircle size={14} className="text-emerald-600" />
                                    Contact
                                  </button>
                                </div>
                                <div className="flex shrink-0 items-center gap-2 border-t border-emerald-100 pt-2 sm:border-0 sm:pt-0">
                                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                                    Snel
                                  </span>
                                  <ContactQuickActions task={task} />
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderProposed = () => (
    <div className="space-y-5">
      <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/90 to-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-bold text-indigo-950">
              <MonitorSmartphone className="h-4 w-4 shrink-0 text-indigo-600" aria-hidden />
              Klaarstaan in klantportaal
            </h3>
            <p className="mt-1 max-w-3xl text-[11px] leading-relaxed text-indigo-900/80">
              Dit zijn sollicitaties waarvoor een recruiter het voorstelpakket heeft gepubliceerd naar het klantportaal
              (samenvatting, match, beschikbaarheid). Open de vacature om het voorstel aan te passen.
            </p>
          </div>
          <span className="shrink-0 rounded-full bg-white px-3 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100">
            {portalClientPreparedApps.length}
          </span>
        </div>
        {engagementAppsLoading ? (
          <p className="text-xs text-slate-500">Laden…</p>
        ) : portalClientPreparedApps.length === 0 ? (
          <p className="rounded-lg border border-dashed border-indigo-200 bg-white/60 px-3 py-4 text-center text-xs text-slate-600">
            Nog geen publicaties naar het klantportaal in deze dataset. Publiceer vanuit de vacature bij status Voorgesteld
            (vink «Klaarzetten in klantportaal»).
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-indigo-100 bg-white">
            <table className="w-full text-left text-[12px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/90 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2">Kandidaat</th>
                  <th className="px-3 py-2 hidden sm:table-cell">Vacature</th>
                  <th className="px-3 py-2 hidden md:table-cell">Gepubliceerd</th>
                  <th className="px-3 py-2">Document</th>
                  <th className="px-3 py-2 min-w-[200px]">Pitch (samenvatting)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {portalClientPreparedApps.map((app) => {
                  const c = app.candidates || app.candidate || {};
                  const v = app.vacancies || app.vacancy || {};
                  const snap = app.cv_snapshot && typeof app.cv_snapshot === 'object' ? (app.cv_snapshot as Record<string, unknown>) : null;
                  const prop = parsePortalClientProposal(app.cv_snapshot);
                  const hasDoc = !!(snap?.portal_client_cv_formatted && typeof snap.portal_client_cv_formatted === 'object');
                  const src =
                    hasDoc && typeof (snap!.portal_client_cv_formatted as { source?: string }).source === 'string'
                      ? (snap!.portal_client_cv_formatted as { source?: string }).source
                      : null;
                  return (
                    <tr key={app.id} className="hover:bg-slate-50/80">
                      <td className="px-3 py-2 font-semibold text-slate-900">{c.name || '—'}</td>
                      <td className="px-3 py-2 hidden sm:table-cell text-slate-700">{v.title || '—'}</td>
                      <td className="px-3 py-2 hidden md:table-cell whitespace-nowrap text-slate-600">
                        {prop?.published_at
                          ? new Date(prop.published_at).toLocaleString('nl-NL', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </td>
                      <td className="px-3 py-2">
                        {hasDoc ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-800 ring-1 ring-emerald-100">
                            {src === 'documentbuilder' ? 'Documentbuilder' : 'Bijlage'}
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {prop?.summary?.trim()
                          ? prop.summary.trim().length > 140
                            ? `${prop.summary.trim().slice(0, 137)}…`
                            : prop.summary.trim()
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {renderEngagementTasksTableShell({
        title: 'Voorstellen',
        subtitle:
          'Status “voorgesteld” met voorgesteld-datum uit cv_snapshot.pipeline_meta. Rijen met >4 dagen op voorstel krijgen een markering.',
        tasks: proposedApplicationTasksFiltered,
        loading: dataLoading || engagementAppsLoading,
        emptyLabelWhenLoaded: 'Geen kandidaten op voorgesteld in deze dataset.',
        showFilterSpacer: false,
      })}
    </div>
  );

  const renderField = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mr-1">Mijlpaal (maanden op opdracht)</span>
        <button
          type="button"
          onClick={() => setFieldMilestoneFilter('all')}
          className={`px-2.5 py-1.5 rounded-lg text-[12px] font-semibold border ${
            fieldMilestoneFilter === 'all'
              ? 'bg-slate-900 text-white border-slate-900'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
        >
          Alle
        </button>
        {(fieldMilestoneMonths.length ? fieldMilestoneMonths : DEFAULT_FIELD_MILESTONE_MONTHS).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setFieldMilestoneFilter(m)}
            className={`px-2.5 py-1.5 rounded-lg text-[12px] font-semibold border ${
              fieldMilestoneFilter === m
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {m} m
          </button>
        ))}
      </div>
      <p className="text-[11px] text-slate-500 max-w-3xl">
        Lopende plaatsingen op exacte volle maanden sinds start (1, 3, 6, 9…). Per klant: in Supabase{' '}
        <code className="text-[10px] bg-slate-100 px-1 rounded">clients.data.field_management_milestones_months</code> (array
        getallen, bijv. <code className="text-[10px] bg-slate-100 px-1 rounded">[1,3,6,9,12]</code>).
      </p>
      {renderEngagementTasksTableShell({
        title: 'Fieldmanagement',
        subtitle: 'Check-in rond vaste intervallen na start van de opdracht.',
        tasks: fieldManagementTasksFiltered,
        loading: dataLoading,
        emptyLabelWhenLoaded: 'Geen actieve opdrachten op een mijlpaal in dit filter.',
        showFilterSpacer: false,
      })}
    </div>
  );

  const renderAvailability = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mr-2">Filter</span>
        <button
          type="button"
          onClick={() => setAvailabilitySubTab('upcoming_month')}
          className={`px-3 py-2 rounded-lg text-[12px] font-semibold border ${
            availabilitySubTab === 'upcoming_month'
              ? 'bg-slate-900 text-white border-slate-900'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
        >
          Binnen 1 maand beschikbaar
        </button>
        <button
          type="button"
          onClick={() => setAvailabilitySubTab('unknown')}
          className={`px-3 py-2 rounded-lg text-[12px] font-semibold border ${
            availabilitySubTab === 'unknown'
              ? 'bg-slate-900 text-white border-slate-900'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
        >
          Onbekende beschikbaarheid
        </button>
        <button
          type="button"
          onClick={() => setAvailabilitySubTab('expiring_availability')}
          className={`px-3 py-2 rounded-lg text-[12px] font-semibold border ${
            availabilitySubTab === 'expiring_availability'
              ? 'bg-slate-900 text-white border-slate-900'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
        >
          Aflopende / vernieuwen
        </button>
      </div>
      {availabilitySubTab === 'upcoming_month'
        ? renderEngagementTasksTableShell({
            title: 'Beschikbaarheid · komende maand',
            subtitle:
              'Kandidaten met een concrete beschikbaarheidsdatum tussen vandaag en ongeveer 30 dagen — om tijdig contact op te nemen.',
            tasks: availabilityUpcomingFiltered,
            loading: dataLoading,
            emptyLabelWhenLoaded: 'Niemand met beschikbaarheid in dit venster (of nog geen datum in het profiel).',
            showFilterSpacer: false,
          })
        : availabilitySubTab === 'unknown'
          ? renderEngagementTasksTableShell({
              title: 'Beschikbaarheid · onbekend',
              subtitle: 'Kandidaten zonder bruikbare beschikbaarheid in het profiel. Vul in het uitklap-paneel de datum in.',
              tasks: availabilityUnknownFiltered,
              loading: dataLoading,
              emptyLabelWhenLoaded: 'Geen kandidaten met onbekende beschikbaarheid (met consent).',
              showFilterSpacer: false,
            })
          : renderEngagementTasksTableShell({
              title: 'Beschikbaarheid · aflopend / vernieuwen',
              subtitle:
                'Kandidaten met een beschikbaarheidsdatum in het verleden tot 14 dagen geleden, of binnen 60 dagen. Check of de datum nog klopt.',
              tasks: availabilityExpiringFiltered,
              loading: dataLoading,
              emptyLabelWhenLoaded: 'Geen kandidaten in dit venster.',
              showFilterSpacer: false,
            })}
    </div>
  );

  const renderBirthday = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mr-2">Verjaardag</span>
        <button
          type="button"
          onClick={() => setBirthdaySubTab('today')}
          className={`px-3 py-2 rounded-lg text-[12px] font-semibold border ${
            birthdaySubTab === 'today'
              ? 'bg-slate-900 text-white border-slate-900'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
        >
          Vandaag
        </button>
        <button
          type="button"
          onClick={() => setBirthdaySubTab('week')}
          className={`px-3 py-2 rounded-lg text-[12px] font-semibold border ${
            birthdaySubTab === 'week'
              ? 'bg-slate-900 text-white border-slate-900'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
        >
          Binnen 7 dagen
        </button>
      </div>
      {renderEngagementTasksTableShell({
        title: birthdaySubTab === 'today' ? 'Verjaardag · vandaag' : 'Verjaardag · deze week',
        subtitle: 'Gebaseerd op geboortedatum uit geparste CV-data.',
        tasks: birthdayTasksFiltered,
        loading: dataLoading,
        emptyLabelWhenLoaded:
          'Geen verjaardagen in dit venster — of geen geboortedatum in de CV-parse.',
        showFilterSpacer: false,
      })}
    </div>
  );

  const renderEnding = () => (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <p className="text-[11px] text-slate-600">
          Gesorteerd op <span className="font-semibold">einddatum</span> (vroegste eerst). Uitklapbare rijen met tijdlijn en snel loggen — detail (klant, opdracht, dagen) staat in <span className="font-semibold">Onderwerp</span> / statuskolom.
        </p>
      </div>
      {renderEngagementTasksTableShell({
        title: 'Eindigende opdrachten',
        subtitle:
          'WhatsApp, e-mail en bellen; klik het pijltje voor communicatie en snelle logging. Kolom Onderwerp bevat klant · opdracht · einddatum.',
        tasks: endingEngagementTasksFiltered,
        loading: dataLoading,
        emptyLabelWhenLoaded:
          'Geen eindigende opdrachten in de komende periode (of geen einddatum bekend).',
        showFilterSpacer: false,
      })}
    </div>
  );

  const renderDataConsent = () => (
    <div className="space-y-3">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 flex items-start gap-2">
        <ShieldAlert size={14} className="shrink-0 mt-0.5" />
        <span>
          Geen automatische verzending: recruiter moet altijd akkoord geven. Kandidaten met opt-out of ontbrekende consent worden niet benaderd vanuit dit scherm.
        </span>
      </div>
      {renderEngagementTasksTableShell({
        title: 'Data & consent',
        subtitle:
          'CV-, beschikbaarheid- en salaris-updates staan in Onderwerp; consentstatus in Status. Uitklappen voor tijdlijn en snel loggen.',
        tasks: dataConsentEngagementTasksFiltered,
        loading: dataLoading,
        emptyLabelWhenLoaded: 'Geen kandidaten om te tonen — talentpool nog leeg of laden.',
        showFilterSpacer: false,
      })}
    </div>
  );

  return (
    <div className="flex h-full w-full min-h-0 bg-[#f8fafc]">
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className={`shrink-0 border-b border-slate-200/90 bg-white ${isCockpitWindow ? 'px-4 py-3' : 'px-5 py-4'}`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <h1 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
                  {isCockpitWindow ? 'Engagement (tweede scherm)' : 'Engagement'}
                </h1>
                <span className="text-[9px] font-black uppercase tracking-widest text-[#e51b23]">BETA</span>
              </div>
              <p className="mt-0.5 max-w-2xl text-[12px] leading-snug text-slate-500">
                {isCockpitWindow
                  ? 'Zelfde overzicht als in de hoofdapp, zonder zijmenu. Terug via de knop hiernaast.'
                  : 'Acties per tab: vandaag opvolgen, website-sollicities, voorstellen, beschikbaarheid en meer.'}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {isCockpitWindow ? (
                <button
                  type="button"
                  onClick={() => {
                    try {
                      window.location.href = `${window.location.origin}/app/candidate-engagement`;
                    } catch {
                      /* ignore */
                    }
                  }}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12px] font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
                >
                  Volledige app
                </button>
              ) : null}
              <button
                type="button"
                onClick={openCockpitSecondWindow}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                title="Nieuw venster, zonder zijbalk — handig op een tweede monitor"
              >
                <MonitorSmartphone size={14} aria-hidden /> Tweede scherm
              </button>
              <button
                type="button"
                onClick={() => void refreshLive()}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-[12px] font-semibold text-white shadow-sm hover:bg-slate-800"
              >
                <Sparkles size={14} aria-hidden /> Vernieuwen
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:flex-wrap sm:items-center">
            <label className="sr-only" htmlFor="wt-engagement-recruiter-filter">
              Recruiterfilter
            </label>
            <select
              id="wt-engagement-recruiter-filter"
              value={filterRecruiterId}
              onChange={(e) => setFilterRecruiterId(e.target.value)}
              title="Optioneel: alleen rijen waarvan de vacature-eigenaar overeenkomt. «Klaar» is lokaal op dit apparaat."
              className="min-w-[12rem] max-w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12px] font-medium text-slate-800 shadow-sm"
            >
              <option value="">Alle recruiters</option>
              {recruiters.map((r) => (
                <option key={r.id} value={r.id}>
                  {(r.name || r.email || r.id).trim()}
                  {r.role === 'ADMIN' ? ' (admin)' : ''}
                </option>
              ))}
            </select>
            <p className="text-[11px] leading-snug text-slate-400 sm:ml-1 sm:flex-1 sm:min-w-[12rem]">
              Filter op vacature-eigenaar. Zonder vacature-link kunnen rijen ontbreken.
            </p>
          </div>

          {/* KPIs — compact */}
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {kpis.map((k) => (
              <div
                key={k.label}
                title={k.hint}
                className="rounded-lg border border-slate-200/90 bg-slate-50/80 px-3 py-2.5"
              >
                <div className="mb-0.5 flex items-center justify-between gap-1">
                  <k.icon size={14} className="shrink-0 text-slate-400" />
                </div>
                <div className="text-lg font-bold tabular-nums leading-tight text-slate-900">{k.value}</div>
                <div className="mt-0.5 text-[10px] font-semibold leading-tight text-slate-600">{k.label}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="mt-4 flex items-center gap-1.5 overflow-x-auto pb-0.5">
            <TabButton id="today" label="Acties vandaag" />
            <TabButton id="open_application" label="Openstaande sollicitatie" badge={websiteWebhookInboxAppsFiltered.length} />
            <TabButton id="proposed" label="Voorstellen" />
            <TabButton id="availability" label="Beschikbaarheid" />
            <TabButton id="ending" label="Eindigende opdrachten" />
            <TabButton id="field" label="Fieldmanagement" />
            <TabButton id="data" label="Data & consent" />
            <TabButton id="birthday" label="Verjaardag" />
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {(loadProgress.active || (loadProgress.percent === 100 && (dataLoading || engagementAppsLoading))) && (
            <div className="mb-4">
              <LoadingBar />
            </div>
          )}
          {!loadProgress.active && (dataLoading || engagementAppsLoading) && (
            <div className="mb-3 inline-flex items-center gap-2 text-[10px] font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1">
              <Loader2 size={10} className="animate-spin text-indigo-500" /> Cache zichtbaar — verversen op achtergrond…
            </div>
          )}
          {mainTab === 'today' && renderTodayActions()}
          {mainTab === 'open_application' && renderOpenApplications()}
          {mainTab === 'proposed' && renderProposed()}
          {mainTab === 'availability' && renderAvailability()}
          {mainTab === 'ending' && renderEnding()}
          {mainTab === 'field' && renderField()}
          {mainTab === 'data' && renderDataConsent()}
          {mainTab === 'birthday' && renderBirthday()}
        </div>
      </div>

      {detailModal && (
        <CandidateDetailModal
          key={`${detailModal.candidateId}-${detailModal.initialTab || 'overview'}`}
          candidateId={detailModal.candidateId}
          vacancyId={detailModal.vacancyId}
          clientId={user.clientId || ''}
          currentUser={user}
          initialTab={detailModal.initialTab ?? 'overview'}
          onClose={() => setDetailModal(null)}
          onUpdate={() => void refreshLive()}
        />
      )}

      <BulkWhatsAppComposerModal
        isOpen={waModalOpen}
        onClose={() => {
          setWaModalOpen(false);
          setActionTask(null);
        }}
        selectedCandidates={bulkSelection}
        vacancyData={selectedVacancy || undefined}
        initialBody={actionTask?.generated_message || ''}
        onSuccessCallback={() => {
          onRefreshPool?.();
          setWaModalOpen(false);
          setActionTask(null);
        }}
      />
      <BulkEmailComposerModal
        isOpen={emailModalOpen}
        onClose={() => {
          setEmailModalOpen(false);
          setActionTask(null);
        }}
        selectedCandidates={bulkSelection}
        vacancyData={selectedVacancy || undefined}
        initialBody={actionTask?.generated_message || ''}
        onSuccessCallback={() => {
          onRefreshPool?.();
          setEmailModalOpen(false);
          setActionTask(null);
        }}
      />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] px-4 py-2 rounded-full bg-slate-900 text-white text-[12px] font-medium shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
};

export default CandidateEngagementCenter;
